<script>
  import { onMount } from 'svelte';
  import Router from 'svelte-spa-router';
  import { initClerk, hasOrgAccess, isOrgAdmin } from './lib/clerk.js';
  import NavBar from './components/NavBar.svelte';
  import OrderList from './routes/OrderList.svelte';
  import OrderDetail from './routes/OrderDetail.svelte';
  import LabelView from './routes/LabelView.svelte';
  import BatchLabels from './routes/BatchLabels.svelte';
  import PackingSlipView from './routes/PackingSlipView.svelte';
  import EcardList from './routes/EcardList.svelte';
  import Analytics from './routes/Analytics.svelte';
  import AdVisits from './routes/AdVisits.svelte';
  import SiteVisits from './routes/SiteVisits.svelte';
  import KeywordDetail from './routes/KeywordDetail.svelte';
  import Annotate from './routes/Annotate.svelte';

  const adminRoutes = {
    '/': OrderList,
    '/order/:id': OrderDetail,
    '/label/:id': LabelView,
    '/labels/:ids': BatchLabels,
    '/packing-slip/:id': PackingSlipView,
    '/ecards': EcardList,
    '/analytics': Analytics,
    '/visits': AdVisits,
    '/site-visits': SiteVisits,
    '/analytics/keywords/:id': KeywordDetail,
    '/annotate': Annotate,
  };
  // Plain org members (annotation helpers) get only the Annotate surface —
  // the server enforces the same split on the API.
  const memberRoutes = {
    '/': Annotate,
    '/annotate': Annotate,
  };

  let ready = false;
  let signedIn = false;
  let hasAccess = false;
  let admin = false;
  $: routes = admin ? adminRoutes : memberRoutes;
  let clerk = null;
  let loadError = null;
  let notConfigured = false;

  onMount(async () => {
    try {
      clerk = await initClerk();
    } catch (e) {
      loadError = e.message;
      ready = true;
      return;
    }
    if (!clerk) {
      notConfigured = true;
      ready = true;
      return;
    }
    refresh();
    clerk.addListener(() => refresh());
    ready = true;
    if (!signedIn) mountSignIn();
  });

  function refresh() {
    signedIn = !!clerk.user;
    hasAccess = signedIn && hasOrgAccess();
    admin = hasAccess && isOrgAdmin();
    if (!signedIn) mountSignIn();
  }

  function mountSignIn() {
    requestAnimationFrame(() => {
      const el = document.getElementById('sign-in');
      if (el && !el.hasChildNodes()) clerk.mountSignIn(el);
    });
  }
</script>

{#if !ready}
  <p class="centered">Loading…</p>
{:else if notConfigured}
  <div class="centered config-error">
    <h1>Clerk not configured</h1>
    <p>Set <code>clerk.publishableKey</code> and <code>clerk.secretKey</code> in <code>config.json</code> (or env vars) and restart.</p>
  </div>
{:else if loadError}
  <div class="centered config-error">
    <h1>Couldn't load sign-in</h1>
    <p>{loadError}</p>
  </div>
{:else if !signedIn}
  <div class="centered"><div id="sign-in"></div></div>
{:else if !hasAccess}
  <div class="centered config-error">
    <h1>Access restricted</h1>
    <p>Your account isn't a member of the Card Hero staff organization.<br>Ask an admin to invite you, then sign in again.</p>
    <button class="signout" on:click={() => clerk.signOut()}>Sign out</button>
  </div>
{:else}
  <NavBar user={clerk.user} {admin} on:signout={() => clerk.signOut()} />
  <main><Router {routes} /></main>
{/if}

<style>
  :global(body) { margin: 0; font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; background: #fafafa; }
  .centered { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 70vh; text-align: center; }
  .config-error code { background: #eee; padding: .1rem .3rem; border-radius: 4px; }
  .signout { margin-top: 1rem; cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .4rem .9rem; border-radius: 6px; }
  main { max-width: 1100px; margin: 0 auto; padding: 1rem; }
</style>
